import type {AiImageAspectRatioOptions, AiImagePanelProps} from '@Panels/ai-image-panel/ai-image-panel.types';
import type {MouseEventHandler, ReactElement} from 'react';
import React, {useRef} from 'react';
import {HeaderSize, Panel, PanelContent, PanelFooter, PanelFooterPrimaryDefaultAction, PanelHeader} from '@Components/panel';
import type {AITextToImageBackendData} from '@Libraries/pmw-stock-media-library';
import {setRecentStockItem} from '@Libraries/pmw-stock-media-library';
import {v4 as uuidv4} from 'uuid';
import {addItemsToGrid, deleteGridItems, getSelectedGridItems, replaceGridItem, toggleGridItemSelection, updateGridItem} from '@Components/base-grid';
import {getGridItemForAIGeneratedImageV2, uploadSelectedItem} from '@Panels/stock-panel/stock-panel.library';
import {TextToImageGrid} from '@Components/text-to-image-grid';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {MIN_STOCK_ITEMS_TO_DISPLAY} from '@Panels/stock-panel/stock-panel.types';
import {SkeletonAnimationType} from '@Components/base-grid/components/grid-skeleton-item';
import type {GridAiImageItemStorage} from '@Components/base-grid/components/grid-stock-image-item';
import {isUserPremium} from '@Libraries/user.library';
import {getGridIdForPanel} from '@Libraries/text-to-image-library';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {MODAL_IDS, openModal} from '@Components/modal-container-deprecated';
import {setRecentStockAvailability} from '@Panels/stock-panel/stock-panel-reducer';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {isMobile} from 'react-device-detect';
import type {TextToImageGridProps} from '@Components/text-to-image-grid/text-to-image-grid.types';
import {AiImageState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import styles from './ai-image-panel.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {updateIsUploadingToMyUploads} from '@/components/poster-editor/poster-editor-reducer';
import {IconSize} from '@Components/icon-v2/icon.types';
import AiImagePromptBox from '@Panels/ai-image-panel/components/ai-image-prompt-box/ai-image-prompt-box';
import {getAiSkeletonItem, getDimensionsForAiImageSkeletonItem} from '@Panels/ai-image-panel/ai-image-panel-library';
import {TextSize} from '@Components/text';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

const SELECTION_ID = 'TEXT_TO_IMAGE_POP_OVER_SELECTION';
const PANEL_ID = 'TEXT_TO_IMAGE_PANEL_FALLBACK_ID';

export default function AiImagePanel({populateRecentlyAddedOnItemAdd = true, ...props}: AiImagePanelProps): ReactElement<any> {
  const selectionIDRef = useRef(props.selectionID ?? SELECTION_ID);
  const gridItemIdsProcessingRef = useRef<Record<string, true>>({});
  const selectedItems = useAppSelector((state) => {
    return getSelectedGridItems(state, selectionIDRef.current);
  });
  const aiImageState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiImage;
  });
  const dispatch = useAppDispatch();
  const panelId = useRef(props.panelId ?? PANEL_ID);
  const gridIDRef = useRef(getGridIdForPanel(panelId.current));
  const aiSkeletonItemIds = useRef<string[]>([]);
  const onClose: () => void = props.onClose
    ? (): void => {
        if (props.onClose) {
          props.onClose();
        }
      }
    : useDefaultPanelClose(panelId.current);

  const onGridItemClicked = (item: GridItemStorage, isDoubleClick: boolean): void => {
    if (isMobile) {
      if (isDoubleClick && selectedItems.length === 1) {
        void onImageClicked([item]);
      } else {
        dispatch(
          toggleGridItemSelection({
            gridItemId: item.id,
            gridSelectionGroupId: selectionIDRef.current,
          })
        );
      }
    } else {
      void onImageClicked([item]);
    }
  };

  const getAIImageGrid = (): ReactElement<any> => {
    const gridProps: TextToImageGridProps = {
      customOnGridItemClicked: onGridItemClicked,
      gridClasses: `spacing-p-t-3 spacing-p-b-3 ${styles.grid}`,
      gridSelectionGroupId: selectionIDRef.current,
      maxSelectionNumber: 1,
      gridId: gridIDRef.current,
      makeContentScrollable: false,
      isLoading: aiImageState === AiImageState.GENERATING,
    };

    return <TextToImageGrid {...gridProps} key={gridIDRef.current} />;
  };

  const getPanelContent = (): ReactElement<HTMLDivElement> => {
    return (
      <div className={'spacing-p-l-4 spacing-p-r-4 spacing-p-t-1'}>
        <AiImagePromptBox
          onImageGenerationStarted={addLoadingSkeletonsToGrid}
          onImageGenerationSuccessful={onGeneratedImagesReceived}
          onImageGenerationFailed={removeLoadingSkeletonsFromGrid}
          mediaType={props.mediaType}
        />
        {getAIImageGrid()}
      </div>
    );
  };

  const onBackButton: MouseEventHandler = (e) => {
    if (props.onBack) {
      props.onBack(e);
    }
  };

  const shouldShowHeader = (): boolean => {
    if (isMobile) {
      return true;
    }
    return !!props.onBack;
  };

  const onImageClicked = async (items: GridItemStorage[]): Promise<void> => {
    onClose();
    showLoading('addStockMediaItem', {
      text: window.i18next.t('pmwjs_adding_1_of_1'),
    });

    let recentStockItemCount = 0;

    for (const item of items) {
      if (item.id in gridItemIdsProcessingRef.current) {
        continue;
      }

      gridItemIdsProcessingRef.current[item.id] = true;
      dispatch(
        updateGridItem({
          gridId: gridIDRef.current,
          itemId: item.id,
          changes: {
            isLoading: true,
          },
        })
      );
      try {
        dispatch(updateIsUploadingToMyUploads(true));

        const onAddItemResponse = await uploadSelectedItem(item, props.shouldLoadDataUrlImage);
        dispatch(updateIsUploadingToMyUploads(false));
        trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_ADDED);
        props.onAddSelected([onAddItemResponse]);

        if (populateRecentlyAddedOnItemAdd && isUserPremium()) {
          recentStockItemCount = await setRecentStockItem(item);
        }
      } catch (e) {
        console.error(e);

        openModal(MODAL_IDS.ERROR_MODAL_ID, {
          message: window.i18next.t('pmwjs_general_ajax_error'),
          log: false,
        });
      }

      dispatch(
        updateGridItem({
          gridId: gridIDRef.current,
          itemId: item.id,
          changes: {
            isLoading: false,
          },
        })
      );

      delete gridItemIdsProcessingRef.current[item.id];
    }

    dispatch(setRecentStockAvailability(recentStockItemCount >= MIN_STOCK_ITEMS_TO_DISPLAY));
    hideLoading('addStockMediaItem');
  };

  const getPrimaryAction = (): ReactElement<any>[] => {
    return [
      <PanelFooterPrimaryDefaultAction
        key="stock-btn-cta-footer"
        text={window.i18next.t('pmwjs_add_selected')}
        onClick={() => {
          void onImageClicked(isMobile ? selectedItems : [selectedItems[0]]);
        }}
      />,
    ];
  };

  const onGeneratedImagesReceived = (response: AITextToImageBackendData[]): void => {
    const items: GridAiImageItemStorage[] = response.map(getGridItemForAIGeneratedImageV2);
    items.forEach((gridItem): void => {
      const skeletonId = aiSkeletonItemIds.current.shift();
      if (skeletonId) {
        dispatch(replaceGridItem({gridId: gridIDRef.current, itemId: skeletonId, gridItem}));
      }
    });
  };

  const addLoadingSkeletonsToGrid = (aspectRatio: AiImageAspectRatioOptions): void => {
    const skeletonItemIds = [uuidv4(), uuidv4()];
    const items = [
      {...getAiSkeletonItem(skeletonItemIds[0]), ...getDimensionsForAiImageSkeletonItem(aspectRatio), animationType: SkeletonAnimationType.AI},
      {...getAiSkeletonItem(skeletonItemIds[1]), ...getDimensionsForAiImageSkeletonItem(aspectRatio), animationType: SkeletonAnimationType.AI},
    ];
    dispatch(
      addItemsToGrid({
        id: gridIDRef.current,
        index: 0,
        items: items,
      })
    );
    aiSkeletonItemIds.current.push(...skeletonItemIds);
  };

  const removeLoadingSkeletonsFromGrid = (): void => {
    dispatch(
      deleteGridItems({
        gridId: gridIDRef.current,
        gridSelectionGroupId: selectionIDRef.current,
        itemIdsToDelete: aiSkeletonItemIds.current.slice(0, 2),
      })
    );
    aiSkeletonItemIds.current.shift();
    aiSkeletonItemIds.current.shift();
  };

  return (
    <Panel
      className={`_full-width _full-height spacing-p-b-4 ${props.className ?? ''}`}
      panelHeader={
        shouldShowHeader() ? (
          <PanelHeader
            backButtonIconSize={!isMobile ? IconSize.SIZE_ICON_16 : undefined}
            titleClasses={`_fit-width content-ai`}
            titleTextSize={TextSize.XSMALL}
            className={`_borderless ${isMobile ? 'text-center' : 'spacing-p-l-4 spacing-p-t-4 spacing-m-b-4'}`}
            size={HeaderSize.SMALL}
            title={window.i18next.t('pmwjs_ai_images')}
            showCloseButton={isMobile}
            onClose={isMobile ? onClose : undefined}
            showBackButton
            onBackButton={onBackButton}
          />
        ) : undefined
      }
      panelContent={<PanelContent className={`_full-width flex-v-row ${styles.panelContent}`}>{getPanelContent()}</PanelContent>}
      panelFooter={selectedItems.length ? <PanelFooter key={selectionIDRef.current} primaryActions={getPrimaryAction()} border fullWidthPrimaryActions /> : undefined}
    />
  );
}
